import React, { FC, ForwardedRef, forwardRef } from 'react';
import { TextField, IconButton, InputAdornment, Icon } from '@mui/material';
import { useEffect } from 'react';
import { InputBaseComponentProps } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
interface TextProps {
    label?: string,
    fieldName: string,
    placeholder?: string,
    value: string | number | undefined,
    multiline?: boolean,
    growWithContent?: boolean,
    onChange: (value: any, fieldName: string) => void,
    className?: string,
    helperText?: string,
    disabled?: boolean,
    readOnly?: boolean,
    step?: string,
    onBlur?: (value: any, fieldName: string) => void,
    type?: 'text' | 'password',
    maxLength?: number,
    error?: string,
    title?: string,
    color?: string,
    focusColor?: string,
    borderColor?: string,
    backgroundColor?: string,
    FormHelperTextProps?: Partial<React.ComponentProps<typeof TextField>['FormHelperTextProps']>,
    onClear?: () => void,
    showClear?: boolean,
    filter?: boolean,
    width?: string,
    onKeyDown?: (event: React.KeyboardEvent) => void,
}

const CheckmateTextInput = forwardRef<HTMLInputElement | null, TextProps>((
    {
        label,
        placeholder,
        value = '',
        fieldName,
        multiline = false,
        growWithContent = false,
        onChange,
        onBlur,
        className = '',
        helperText = "",
        disabled = false,
        readOnly = false,
        type = 'text',
        maxLength,
        error = '',
        title = '',
        color = 'default',
        focusColor = 'default',
        borderColor = 'default',
        backgroundColor = 'inherit',
        width = 'default',
        FormHelperTextProps,
        onClear,
        showClear,
        filter = false,
        onKeyDown,
    }, 
    ref
) => {

    useEffect(() => {
        if (readOnly) {
            onChange(value, fieldName);
        }
    }, [value])

    const inputProps: InputBaseComponentProps = {
        readOnly
    };
    if (maxLength) {
        inputProps.maxLength = maxLength;
    }
    const extraProps: any = { }
    if (multiline) {
        if (growWithContent){
            extraProps.minRows = 5; 
        } else {
            extraProps.rows = 12;
        }
    }

    return <TextField
        className={`form-control ${className}`}
        multiline={multiline}
        inputRef={ref}
        label={label}
        variant={'outlined'}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value, fieldName)}
        value={value}
        size="small"
        helperText={error ? error : helperText}
        FormHelperTextProps={FormHelperTextProps}
        error={!!error.length}
        type={type}
        disabled={disabled}
        inputProps={inputProps}
        onBlur={(e) => onBlur && onBlur(e.target.value, fieldName)}
        title={title}
        sx={{
            backgroundColor: backgroundColor,
            borderColor: borderColor,
            color: color,
            width: width,
            '& .MuiOutlinedInput-root': {
                '& fieldset': {
                    borderColor: `${borderColor || 'default'} !important`,
                },
                '&:hover fieldset': {
                    borderColor: `${focusColor || 'default'} !important`,
                },
                '&.Mui-focused fieldset': {
                    borderColor: `${focusColor || 'default'} !important`,
                },
                '&.Mui-error fieldset': {
                    borderColor: '#d32f2f !important'
                },
                '& legend': {
                    width: 'auto',
                    color: 'none',
                },
            },
            '& .MuiInputLabel-root': {
                color: `${borderColor || 'default'} !important`,
                '&.Mui-focused': {
                    color: `${focusColor || 'default'} !important`,
                },
                '&.Mui-error': {
                    color: '#d32f2f !important'
                }
            },
            '& .MuiInputBase-input': {
                color: `${color || 'default'} !important`,
            },
            '& .MuiFormHelperText-root': {
                color: `${color || 'default'} !important`,
                '&.Mui-error': {
                    color: '#d32f2f !important'
                }
            },
        }}
        {...extraProps}
        InputProps={{
            ...inputProps,
            startAdornment: filter && (
                //make it so it doesnt change on hover and when u click it there is no background color
                <InputAdornment position="start" className='start-adornment'>
                    <IconButton size='small' edge='start' color='primary' onClick={() => {}} sx={{
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                        '&:active': {
                            backgroundColor: 'transparent',
                        },
                        '&:focus': {
                            backgroundColor: 'transparent',
                        },
                    }}>
                        <FilterListIcon />
                    </IconButton>
                </InputAdornment>
            ),
            endAdornment: showClear && (
                <InputAdornment position="end" className='clear-icon-wrapper'>
                    <IconButton onClick={onClear} edge="end" size="small" color="primary">
                        <ClearIcon fontSize="small" />
                    </IconButton>
                </InputAdornment>
            )
        }}
        onKeyDown={onKeyDown}
    >
    </TextField>;
})

export default CheckmateTextInput;